import {
  FontAwesomeIcon as Icon,
  FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';
import { FC } from 'react';
import styled, { css } from 'styled-components';
import { Color } from '../../styles';
import { CenterAlignedBox } from '../layout/layout';

type BaseIconProps = Omit<FontAwesomeIconProps, 'size'> & {
  size?: FontAwesomeIconProps['size'] | number;
  iconSize?: never;
  noMargin?: boolean;
};
const StyledIcon = styled(Icon)<BaseIconProps>`
  ${({ size = 16 }) =>
    typeof size === 'number'
      ? css`
          width: ${size}px;
          height: ${size}px;
        `
      : ''};
`;

type IconSize = 'middle' | 'large' | number;

type ColorIconProps = Omit<BaseIconProps, 'size' | 'iconSize'> & {
  color: Color | 'transparent';
  isGradient?: boolean;
  className?: string;
  size?: IconSize;
  iconSize?: BaseIconProps['size'];
  noMargin?: boolean;
};
type IconProps = BaseIconProps | ColorIconProps;
const isColorIconProps = (props: IconProps): props is ColorIconProps =>
  'color' in props;
const Wrapper = styled(CenterAlignedBox)<{
  $color: Color | 'transparent';
  $isGradient?: boolean;
  $size?: IconSize;
}>`
  justify-content: center;
  --icon-wrapper-width: 16px;
  --icon-wrapper-height: 16px;
  ${({ $size }) =>
    typeof $size === 'number'
      ? css`
          --icon-wrapper-width: ${$size}px;
          --icon-wrapper-height: ${$size}px;
        `
      : $size === 'large'
        ? css`
            --icon-wrapper-width: 24px;
            --icon-wrapper-height: 24px;
          `
        : ''};
  min-width: var(--icon-wrapper-width);
  width: var(--icon-wrapper-width);
  height: var(--icon-wrapper-height);
  ${({ $color, $isGradient }) =>
    $color === 'transparent'
      ? css`
          background: transparent;
        `
      : css`
          background: var(
            --icon-${$isGradient ? 'gradient-' : ''}background-${$color}
          );
        `};
  color: ${({ $color }) =>
    $color === 'transparent' ? 'inherit' : 'var(--text-on-color)'};
  border-radius: var(--border-radius-default);
`;
const IconComponent: FC<IconProps> = (props) => {
  if (isColorIconProps(props)) {
    const { color, isGradient, className, size, iconSize, ...rest } = props;

    return (
      <Wrapper
        $color={color}
        $isGradient={isGradient}
        className={className}
        $size={size}
      >
        <StyledIcon {...rest} size={iconSize ?? 12} />
      </Wrapper>
    );
  }

  return <StyledIcon {...props} />;
};

export const FontAwesomeIcon = styled(IconComponent)<IconProps>``;
